/**
 * WebSocket Hook for Real-Time Match Logging
 * Based on Section 4.3: Data Synchronization Flow (Offline-First Strategy)
 *
 * Implements:
 * - WebSocket connection lifecycle (onopen, onclose, onmessage)
 * - syncQueue: Syncs queued events on reconnection
 * - sendEvent: Sends event or queues if offline
 * - Automatic reconnection handling
 */
import { useEffect, useRef, useCallback } from "react";
import {
  useMatchLogStore,
  MatchEvent,
  PendingAckEntry,
} from "../store/useMatchLogStore";
import { auth } from "../lib/firebase";
import { trackTelemetry } from "../lib/telemetry";

const IS_E2E_TEST_MODE = import.meta.env.VITE_E2E_TEST_MODE === "true";

// Task #37: bounds handleAckResult's error branch, which used to re-queue
// a rejected event unconditionally, forever — a payload permanently
// rejected by backend validation hits the identical error on every retry
// (app/websocket.py's handle_event catches a Pydantic ValidationError and
// a transient DB hiccup in the same broad `except Exception`, so the
// frontend cannot yet tell them apart from the ack alone). This counter
// only measures genuine server-side rejections: an error ack means the
// payload was received and rejected, never a dropped connection — a
// disconnected send takes the queue path directly and retries on
// reconnect without ever reaching handleAckResult's error branch. So 5
// attempts is 5 real rejections, not 5 failed connections, which is what
// makes the cap defensible rather than arbitrary. At syncQueue's 10s
// resend interval, 5 attempts is ~50s: long enough to not be tripped by a
// single resend cycle's own transient blip, short enough that a
// genuinely poisoned event stops hammering the backend well within a
// minute instead of for the rest of the session.
const MAX_RETRY_COUNT = 5;

// PMA-COCK-147 (cockpit ack-latency wave): the ONLY timeout that used to
// exist anywhere in this hook was `reconnectTimeoutRef`, which guards the
// WebSocket *transport* reconnecting after a close -- nothing ever expired
// an individual `pendingAcks` entry. A single lost ack (server received the
// event and even persisted it, but the ack response itself never made it
// back -- a dropped packet, a proxy timeout, anything short of the socket
// actually closing) left `computeIsSubmitting(pendingAcks)` true forever,
// permanently disabling the cockpit's action panels until a reload.
// Production evidence (team_plan.md): the backend's own per-event ack floor
// grew past 5.7s late in a long, heavily-logged match well before this fix
// -- 20s is comfortable headroom above that measured worst case (and Lane
// A's backend fix removes the O(n^2) recompute that caused it in the first
// place), while still being short enough that a genuinely dropped ack does
// not leave the operator staring at a dead panel for the rest of the match.
// See `expireAck` below for what happens when this fires, and #63/#78's
// documented duplicate_filter reasoning (this file and
// app/websocket.py's handle_event) for why re-sending under the SAME
// client_id, not a fresh one, is what keeps that safe.
export const ACK_TIMEOUT_MS = 20000;

const resolveWsBaseUrl = () => {
  const explicit = import.meta.env.VITE_WS_URL;
  if (explicit) {
    return explicit.replace(/\/+$/, "");
  }

  const apiUrl = import.meta.env.VITE_API_URL;
  if (apiUrl) {
    const sanitized = apiUrl.replace(/\/+$/, "");
    const withoutApi = sanitized.replace(/\/api\/v1$/, "");
    if (withoutApi.startsWith("https://")) {
      return withoutApi.replace("https://", "wss://");
    }
    if (withoutApi.startsWith("http://")) {
      return withoutApi.replace("http://", "ws://");
    }
  }

  if (typeof window !== "undefined") {
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    return `${protocol}//${window.location.host}`;
  }

  return "ws://localhost:8000";
};

const generateClientId = (): string => {
  const globalCrypto = globalThis.crypto as Crypto | undefined;
  if (globalCrypto?.randomUUID) {
    return globalCrypto.randomUUID();
  }
  return `client-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const MATCH_CLOCK_PATTERN = /^\d{1,3}:\d{2}(?:\.\d{1,3})?$/;

const normalizeMatchClockForSocket = (value: string): string => {
  const raw = String(value || "").trim();
  if (!raw) return "00:00.000";

  if (MATCH_CLOCK_PATTERN.test(raw)) {
    if (raw.includes(".")) {
      const [minutesPart, rest] = raw.split(":");
      const [secondsPart, millisPart] = rest.split(".");
      return `${Number(minutesPart)}:${String(Number(secondsPart)).padStart(
        2,
        "0",
      )}.${(millisPart || "0").slice(0, 3).padEnd(3, "0")}`;
    }
    const [minutesPart, secondsPart] = raw.split(":");
    return `${Number(minutesPart)}:${String(Number(secondsPart)).padStart(
      2,
      "0",
    )}.000`;
  }

  if (/^\d+$/.test(raw)) {
    // Recover stripped clocks like "4500000" => "45:00.000"
    if (raw.length >= 6) {
      const millis = Number(raw.slice(-3));
      const seconds = Number(raw.slice(-5, -3));
      const minutes = Number(raw.slice(0, -5) || "0");
      if (seconds <= 59) {
        return `${minutes}:${String(seconds).padStart(2, "0")}.${String(
          millis,
        ).padStart(3, "0")}`;
      }
    }

    const totalSeconds = Number(raw);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, "0")}.000`;
  }

  return "00:00.000";
};

interface UseMatchSocketProps {
  matchId: string;
  enabled?: boolean;
}

interface UseMatchSocketReturn {
  // client_id excluded (task #46/#53 finding): sendEvent's implementation
  // unconditionally overwrites client_id with a fresh generateClientId()
  // call, discarding any caller-supplied value -- the type used to still
  // list it as accepted, which is exactly the footgun that caught both the
  // author of duplicate-events.spec.ts's fixed client_id literals and
  // whoever later widened that test's assertions to tolerate the resulting
  // non-dedup rather than ask why. Type-only change, zero runtime
  // difference: a new dispatch is correctly a new logical event with a
  // fresh identity. Preserving a caller's client_id for a genuine resend is
  // ensureClientId's job (used by the queue-flush/pending-ack path), not
  // sendEvent's -- see that function for the retry-preserving half of this
  // same distinction.
  sendEvent: (
    event: Omit<MatchEvent, "match_id" | "timestamp" | "client_id">,
  ) => void;
  undoEvent: (event: MatchEvent) => void;
  isConnected: boolean;
}

interface DuplicateMetadata {
  event_id?: string | null;
  match_id?: string;
  match_clock?: string;
  period?: number;
  team_id?: string;
}

interface AckResultPayload {
  status?: string;
  event_id?: string;
  duplicate?: DuplicateMetadata;
  client_id?: string;
  message?: string;
}

interface SocketTestHarness {
  forceDisconnect: () => void;
  reconnect: () => void;
}

declare global {
  interface Window {
    __PROMATCH_SOCKET_TEST__?: SocketTestHarness;
  }
}

/**
 * Custom hook for WebSocket connection to match logging endpoint
 * Implements Section 4.3: Data Synchronization Flow
 */
export const useMatchSocket = ({
  matchId,
  enabled = true,
}: UseMatchSocketProps): UseMatchSocketReturn => {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();
  const pendingAckOrderRef = useRef<string[]>([]);
  const suppressReconnectRef = useRef(false);
  const reconnectAttemptRef = useRef(0);
  // #93: counts every WebSocket construction this hook instance has made,
  // incremented in connect() itself (not in handleOpen/onclose) so it
  // covers a connect() call reached by ANY path -- the auto-reconnect
  // backoff, an explicit E2E-harness reconnect(), AND a React StrictMode
  // dev-mode remount (disconnect() nulls ws.onclose before closing, so that
  // teardown never reaches onclose's own bookkeeping). A value > 1 at
  // handleOpen time means this is not the first WebSocket this hook
  // instance has opened -- see handleOpen's wasReconnect for why that is
  // the rehydrate trigger.
  const connectAttemptCountRef = useRef(0);

  const {
    isConnected,
    setConnected,
    queuedEventsByMatch,
    pendingAcks,
    addLiveEvent,
    upsertLiveEvent,
    removeLiveEventByTimestamp,
    removeLiveEventByClientId,
    removeLiveEventById,
    addQueuedEvent,
    removeQueuedEvent,
    removeQueuedEventByClientId,
    addFailedEvent,
    upsertPendingAck,
    upsertPendingAcks,
    resolvePendingAck,
    rejectPendingAck,
    setDuplicateHighlight,
    incrementDuplicateStats,
    setQueuedEventClientId,
    requestTimelineRefresh,
    pushUndoCandidate,
    removeUndoCandidate,
    requestMatchRefresh,
  } = useMatchLogStore();

  const queuedEventsRef =
    useRef<Record<string, MatchEvent[]>>(queuedEventsByMatch);
  const pendingAcksRef = useRef<typeof pendingAcks>(pendingAcks);
  const undoInFlightRef = useRef<Set<string>>(new Set());
  const undoCompletedRef = useRef<Set<string>>(new Set());

  // PMA-COCK-147: per-clientId ack-expiry timers (see ACK_TIMEOUT_MS above).
  const ackTimeoutsRef = useRef<Record<string, ReturnType<typeof setTimeout>>>(
    {},
  );

  const clearAckTimeout = useCallback((clientId: string) => {
    const timer = ackTimeoutsRef.current[clientId];
    if (timer) {
      clearTimeout(timer);
      delete ackTimeoutsRef.current[clientId];
    }
  }, []);

  const clearAllAckTimeouts = useCallback(() => {
    Object.values(ackTimeoutsRef.current).forEach(clearTimeout);
    ackTimeoutsRef.current = {};
  }, []);

  // PMA-COCK-147: fires when a pendingAcks entry's ack never arrived within
  // ACK_TIMEOUT_MS. Must (a) always unblock computeIsSubmitting -- a lost
  // ack can never permanently disable the cockpit -- and (b) never orphan
  // OR double-log the underlying event.
  const expireAck = useCallback(
    (clientId: string) => {
      delete ackTimeoutsRef.current[clientId];

      // Keep FIFO order consistent for handleAckResult's no-client_id
      // fallback (`pendingAckOrderRef.current.shift()`) -- an entry that
      // expires must not linger in that order list after it has been
      // pulled out of pendingAcks below.
      const idx = pendingAckOrderRef.current.indexOf(clientId);
      if (idx !== -1) {
        pendingAckOrderRef.current.splice(idx, 1);
      }

      const wasUndo = undoInFlightRef.current.has(clientId);
      // Pulls the entry out of the store's pendingAcks -- this alone is
      // what makes computeIsSubmitting(pendingAcks) false again, unblocking
      // action entry regardless of what happens below.
      const entry = rejectPendingAck(clientId);
      if (!entry) {
        // Already resolved (a real ack landed in the same tick this timer
        // fired) -- nothing left to do.
        return;
      }

      trackTelemetry("ws_ack_timeout", {
        matchId,
        eventType: entry.event.type,
        source: entry.source,
        isUndo: wasUndo,
        ackTimeoutMs: ACK_TIMEOUT_MS,
      });

      if (wasUndo) {
        // #63/#78 backstop, undo variant: entry.event here is the ORIGINAL
        // event being deleted, not a new one to log. Re-queuing it via
        // addQueuedEvent (the normal-send path below) would resend it as a
        // fresh Shot/Pass/whatever -- silently resurrecting a deleted event
        // as a brand-new duplicate. Only clear the tracking so the gate
        // unblocks; a still-unconfirmed undo needs a manual retry, not an
        // automatic one that could double-log.
        undoInFlightRef.current.delete(clientId);
        console.warn(
          "⚠ Undo ack timed out after",
          ACK_TIMEOUT_MS,
          "ms — cockpit unblocked, but the undo was not confirmed. Retry Undo manually if the event is still present.",
          clientId,
        );
        return;
      }

      // Normal send/queue-resend timeout: requeue so the existing retry
      // path (10s periodic syncQueue -- see the `setInterval` effect below
      // -- and on next reconnect) picks it back up exactly like any other
      // offline-queued event. Deliberately does NOT touch _retryCount
      // (MAX_RETRY_COUNT) -- that counter is reserved for a genuine server
      // REJECTION (an error ack), never a no-response; an ack timeout may
      // legitimately retry until it lands. ensureClientId (called from
      // syncQueue) preserves the SAME client_id already stamped on
      // entry.event, so if the original send actually DID land and only
      // its ack was lost -- the case this production incident is about --
      // the backend's exact-client_id duplicate_filter (#63/#78,
      // app/websocket.py handle_event) returns "duplicate" for the resend
      // instead of double-logging the event.
      //
      // PMA-COCK-205: an entry's `source` says where the event ALREADY
      // lives while its ack was pending, and that determines what "requeue"
      // must do -- mirrors handleAckResult's error-ack branch (below),
      // which already gets this right:
      //   - source "live": the event is optimistically in `liveEvents`
      //     ONLY (registerPendingAck's normal send path) -- it must be
      //     removed from there FIRST, or it ends up double-membered across
      //     `liveEvents` AND `queuedEvents` at once, which every
      //     `[...liveEvents, ...queuedEvents]` consumer folds with no
      //     identity check (useDisciplinary.ts's `yellowCount >= 2` gate
      //     being the sharpest consequence -- one real yellow card reads as
      //     two, one short of a false dismissal).
      //   - source "queue": the event never left `queuedEventsByMatch` in
      //     the first place (syncQueue's resend loop sends it but does not
      //     remove it -- see `pendingQueueTimestamps`'s comment above) --
      //     calling `addQueuedEvent` here would push a SECOND copy of the
      //     same object into the queue instead. Nothing to add; the next
      //     syncQueue tick or reconnect resends the one copy already there
      //     now that it is no longer "pending".
      if (entry.source === "live") {
        if (entry.event.client_id) {
          removeLiveEventByClientId(entry.event.client_id);
        } else {
          removeLiveEventByTimestamp(entry.event.timestamp);
        }
        addQueuedEvent(entry.event);
        console.warn(
          "⚠ Ack timed out after",
          ACK_TIMEOUT_MS,
          "ms — event re-queued for retry:",
          entry.event.type,
          clientId,
        );
      } else {
        console.warn(
          "⚠ Ack timed out after",
          ACK_TIMEOUT_MS,
          "ms — resend still queued for retry:",
          entry.event.type,
          clientId,
        );
      }
    },
    [
      rejectPendingAck,
      addQueuedEvent,
      matchId,
      removeLiveEventByClientId,
      removeLiveEventByTimestamp,
    ],
  );

  const startAckTimeout = useCallback(
    (clientId: string) => {
      clearAckTimeout(clientId);
      ackTimeoutsRef.current[clientId] = setTimeout(() => {
        expireAck(clientId);
      }, ACK_TIMEOUT_MS);
    },
    [clearAckTimeout, expireAck],
  );

  const getAuthToken = useCallback(async (): Promise<string | null> => {
    if (IS_E2E_TEST_MODE && typeof window !== "undefined") {
      const token = (window as any).__PROMATCH_E2E_TOKEN__ ?? "e2e-test-token";
      return token;
    }

    const user = auth.currentUser;
    if (!user) {
      console.error("No authenticated user");
      return null;
    }
    try {
      // Force-refresh the token on every (re)connect so an expired token
      // after a >1h match session does not cause an infinite reconnect loop
      // with a stale JWT.
      return await user.getIdToken(true);
    } catch (error) {
      console.error("Failed to get auth token", error);
      return null;
    }
  }, []);

  const ensureClientId = useCallback(
    (event: MatchEvent, source: "live" | "queue") => {
      if (event.client_id) {
        return event.client_id;
      }
      const generated = generateClientId();
      if (source === "queue") {
        setQueuedEventClientId(event.match_id, event.timestamp, generated);
      }
      event.client_id = generated;
      return generated;
    },
    [setQueuedEventClientId],
  );

  const registerPendingAck = useCallback(
    (event: MatchEvent, source: "live" | "queue") => {
      const clientId = ensureClientId(event, source);
      if (!pendingAckOrderRef.current.includes(clientId)) {
        pendingAckOrderRef.current.push(clientId);
      }
      upsertPendingAck(clientId, { event, source });
      // PMA-COCK-147: every registered pending ack gets its own expiry so a
      // lost ack can never permanently disable the cockpit -- see
      // ACK_TIMEOUT_MS/expireAck above.
      startAckTimeout(clientId);
    },
    [ensureClientId, upsertPendingAck, startAckTimeout],
  );

  const handleUndoAck = useCallback(
    (ackResult: AckResultPayload) => {
      const clientId = ackResult?.client_id ?? null;
      if (clientId) {
        // PMA-COCK-147: a real response arrived -- cancel that clientId's
        // ack-expiry timer before it can fire a stray (and, for undo,
        // no-op-only) expireAck later.
        clearAckTimeout(clientId);
        const idx = pendingAckOrderRef.current.indexOf(clientId);
        if (idx !== -1) {
          pendingAckOrderRef.current.splice(idx, 1);
        }
      }

      const entry = clientId ? resolvePendingAck(clientId) : undefined;
      undoInFlightRef.current.delete(clientId ?? "");
      undoCompletedRef.current.add(clientId ?? "");

      if (ackResult?.status === "undo_success") {
        if (ackResult?.event_id) {
          removeLiveEventById(ackResult.event_id);
        }
        if (clientId) {
          removeLiveEventByClientId(clientId);
          removeQueuedEventByClientId(clientId);
          removeUndoCandidate(clientId);
        }
      } else if (ackResult?.status === "undo_not_found") {
        console.warn("Undo target not found for client id", clientId);
      } else {
        console.error(
          "Undo request failed",
          ackResult?.message || "Unknown undo error",
        );
      }

      if (!entry && clientId) {
        // Already resolved elsewhere (e.g., regular ACK beat undo response).
        resolvePendingAck(clientId);
      }
    },
    [
      removeLiveEventById,
      removeLiveEventByClientId,
      removeQueuedEventByClientId,
      removeUndoCandidate,
      resolvePendingAck,
      clearAckTimeout,
    ],
  );

  const handleAckResult = useCallback(
    (ackResult: AckResultPayload) => {
      let pendingId = ackResult?.client_id ?? null;
      const status = ackResult?.status ?? "success";

      if (status.startsWith("undo_")) {
        handleUndoAck(ackResult);
        return;
      }

      if (pendingId) {
        // PMA-COCK-147: a real ack arrived for this clientId -- cancel its
        // expiry timer before it can fire a stray (and, for a "success"
        // status, incorrect) requeue later.
        clearAckTimeout(pendingId);
        const idx = pendingAckOrderRef.current.indexOf(pendingId);
        if (idx !== -1) {
          pendingAckOrderRef.current.splice(idx, 1);
        }
      } else {
        pendingId = pendingAckOrderRef.current.shift() ?? null;
        if (pendingId) {
          clearAckTimeout(pendingId);
        }
      }

      if (!pendingId) {
        if (status === "success") {
          requestTimelineRefresh();
        } else {
          console.warn(
            "Ack received with no pending event in queue",
            ackResult,
          );
        }
        return;
      }
      const entry =
        status === "success"
          ? resolvePendingAck(pendingId)
          : rejectPendingAck(pendingId);

      if (!entry) {
        if (pendingId && undoCompletedRef.current.has(pendingId)) {
          undoCompletedRef.current.delete(pendingId);
          return;
        }
        console.warn("Pending ack entry missing for id", pendingId);
        return;
      }

      const { event, source } = entry;

      if (status === "success") {
        if (ackResult?.event_id) {
          upsertLiveEvent({
            ...event,
            _id: ackResult.event_id,
            _confirmed: true,
          });
        }
        if (source === "queue") {
          removeQueuedEvent(event);
        }
        if (event.client_id) {
          pushUndoCandidate(event.client_id);
        }
        console.log("✓ Event persisted:", event.type, ackResult?.event_id);
      } else if (status === "duplicate") {
        if (event.client_id) {
          removeLiveEventByClientId(event.client_id);
        } else {
          removeLiveEventByTimestamp(event.timestamp);
        }
        if (source === "queue") {
          removeQueuedEvent(event);
        }
        if (event.client_id) {
          removeUndoCandidate(event.client_id);
        }
        const duplicateMeta = ackResult?.duplicate;
        setDuplicateHighlight({
          match_clock: duplicateMeta?.match_clock ?? event.match_clock,
          period: duplicateMeta?.period ?? event.period,
          team_id: duplicateMeta?.team_id ?? event.team_id,
          timestamp: event.timestamp,
          created_at: new Date().toISOString(),
          existing_event_id: duplicateMeta?.event_id ?? null,
        });
        incrementDuplicateStats({
          match_clock: event.match_clock,
          period: event.period,
          team_id: event.team_id,
          type: event.type,
        });
        trackTelemetry("match_event_duplicate", {
          matchId,
          eventType: event.type,
          period: event.period,
          matchClock: event.match_clock,
          teamId: event.team_id,
          source,
        });
        console.info(
          "⚠ Duplicate event acknowledged, removed optimistic entry",
        );
      } else {
        // Error ack (task #37): the server received and rejected this
        // event — remove it from wherever it currently lives (live or
        // queue) and either re-queue with an incremented _retryCount, or,
        // once that count reaches MAX_RETRY_COUNT, move it to the
        // terminal failedEvents state instead of back into the retry
        // queue. See MAX_RETRY_COUNT's own comment for why counting only
        // error acks (never dropped connections) makes the cap
        // defensible.
        const retryCount = (event._retryCount ?? 0) + 1;
        const updatedEvent: MatchEvent = { ...event, _retryCount: retryCount };

        if (source === "live") {
          if (event.client_id) {
            removeLiveEventByClientId(event.client_id);
          } else {
            removeLiveEventByTimestamp(event.timestamp);
          }
        } else {
          removeQueuedEvent(event);
        }

        if (retryCount >= MAX_RETRY_COUNT) {
          addFailedEvent(updatedEvent);
          console.error(
            `✗ Event permanently failed after ${retryCount} server rejections, moved to failedEvents`,
            status,
          );
        } else {
          addQueuedEvent(updatedEvent);
          console.warn(
            "✗ Event failed, re-queued for retry",
            status,
            `attempt ${retryCount}/${MAX_RETRY_COUNT}`,
          );
        }
      }
    },
    [
      resolvePendingAck,
      rejectPendingAck,
      upsertLiveEvent,
      removeQueuedEvent,
      removeLiveEventByTimestamp,
      removeLiveEventByClientId,
      addQueuedEvent,
      addFailedEvent,
      setDuplicateHighlight,
      incrementDuplicateStats,
      matchId,
      requestTimelineRefresh,
      handleUndoAck,
      pushUndoCandidate,
      removeUndoCandidate,
      clearAckTimeout,
    ],
  );

  const flushPendingAcksToQueue = useCallback(() => {
    while (pendingAckOrderRef.current.length) {
      const pendingId = pendingAckOrderRef.current.shift();
      if (!pendingId) {
        continue;
      }
      const entry = rejectPendingAck(pendingId);
      if (entry && entry.source === "live") {
        // PMA-COCK-205: remove the optimistic entry from `liveEvents`
        // BEFORE requeuing it -- same reasoning as `expireAck` above (this
        // file) -- so a reconnect never leaves the same event double-
        // membered across `liveEvents` AND `queuedEvents` at once. A
        // "queue"-source entry (a resend-in-flight) needs no action here:
        // it never left `queuedEventsByMatch` in the first place, so
        // re-adding it would duplicate it within that single array
        // instead.
        if (entry.event.client_id) {
          removeLiveEventByClientId(entry.event.client_id);
        } else {
          removeLiveEventByTimestamp(entry.event.timestamp);
        }
        addQueuedEvent(entry.event);
      }
    }
    // PMA-COCK-147: every pendingAcks entry was just flushed back to the
    // queue above (a real transport disconnect, not an ack timeout) -- any
    // still-scheduled ack-expiry timers for those same clientIds are now
    // stale. expireAck would already no-op safely on them (rejectPendingAck
    // finds nothing left), but clearing them here avoids leaving live
    // timers referencing a hook instance that may be mid-teardown (this is
    // also called from disconnect()).
    clearAllAckTimeouts();
  }, [
    rejectPendingAck,
    addQueuedEvent,
    clearAllAckTimeouts,
    removeLiveEventByClientId,
    removeLiveEventByTimestamp,
  ]);

  /**
   * Sync Queue Function (Section 4.3, Step 6)
   * Sends all queued events and removes them on success
   */
  useEffect(() => {
    queuedEventsRef.current = queuedEventsByMatch;
  }, [queuedEventsByMatch]);

  useEffect(() => {
    pendingAcksRef.current = pendingAcks;
  }, [pendingAcks]);

  const syncQueue = useCallback(() => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      return;
    }

    const scopedQueue = matchId ? queuedEventsRef.current[matchId] || [] : [];

    if (!scopedQueue.length) {
      return;
    }

    const pendingQueueTimestamps = new Set(
      (Object.values(pendingAcksRef.current || {}) as PendingAckEntry[])
        .filter((entry) => entry.source === "queue")
        .map((entry) => entry.event.timestamp),
    );

    console.log(
      `Syncing ${scopedQueue.length} queued events for match ${matchId}...`,
    );

    // Registrations are collected here and flushed to the store in ONE
    // upsertPendingAcks call after the loop, instead of one upsertPendingAck
    // store write per event. A large offline queue (e.g. a match logged
    // through bad stadium wifi, then reconnected) would otherwise drive N
    // synchronous store writes back-to-back with no yield between them,
    // which can trip React's nested-update guard. pendingAckOrderRef is
    // still updated inline, in send order, each iteration — it is a plain
    // ref (no render), and handleAckResult's no-client_id fallback relies on
    // its FIFO order matching send order. wsRef.current.send() is
    // synchronous and the whole loop never awaits, so no ack for an
    // earlier-sent event can arrive (and need pendingAcks) before this
    // function returns and flushes the batch — nothing here is sent without
    // being registered by the time control leaves this call.
    const registrations: Record<string, PendingAckEntry> = {};

    for (const event of scopedQueue) {
      // #63/#78: this guard is the reason a queued event's client_id (see
      // ensureClientId below) is normally never sent twice -- an event
      // still awaiting its ack has a live pendingAcks entry, so a second
      // syncQueue pass (e.g. a follow-up disconnect/reconnect) skips it
      // instead of re-flushing it. That is also why the backend's
      // exact-client_id duplicate-detection branch (app/websocket.py,
      // handle_event's `if event.client_id:` filter) is not reachable
      // through normal client operation today. It DOES become reachable if
      // this guard's precondition fails -- i.e. if the pendingAcks entry
      // for this event is consumed (resolvePendingAck) without the event
      // itself ever being resolved -- which is exactly what #78 describes
      // (a duplicate or out-of-order ack consuming an entry that belongs to
      // a still-outstanding send). If #78 is fixed, this branch's
      // reachability drops again; until then it is the real backstop
      // against a silent double-log from that exact failure mode.
      if (pendingQueueTimestamps.has(event.timestamp)) {
        continue;
      }

      try {
        const clientId = ensureClientId(event, "queue");
        event.match_clock = normalizeMatchClockForSocket(event.match_clock);
        wsRef.current.send(JSON.stringify(event));
        if (!pendingAckOrderRef.current.includes(clientId)) {
          pendingAckOrderRef.current.push(clientId);
        }
        registrations[clientId] = { event, source: "queue" };
        console.log("↻ Queue event sent for ack:", event.type);
      } catch (error) {
        console.error("✗ Failed to sync event:", error);
        break; // Stop syncing on error
      }
    }

    upsertPendingAcks(registrations);
    // PMA-COCK-147: each freshly (re)registered pending ack gets its own
    // expiry timer too -- a resend that itself gets no ack must not be
    // exempt from the same "never permanently disable the cockpit"
    // guarantee as a first-time send.
    Object.keys(registrations).forEach(startAckTimeout);
  }, [upsertPendingAcks, matchId, ensureClientId, startAckTimeout]);

  /**
   * Send Event Function
   * Implements Section 4.3, Steps 7-9
   * - If connected: send via WebSocket
   * - If not connected or send fails: queue to IndexedDB
   */
  const sendEvent = useCallback(
    (eventData: Omit<MatchEvent, "match_id" | "timestamp" | "client_id">) => {
      const event: MatchEvent = {
        ...eventData,
        match_clock: normalizeMatchClockForSocket(eventData.match_clock),
        match_id: matchId,
        timestamp: new Date().toISOString(),
        client_id: generateClientId(),
      };

      // Step 8: Optimistic UI - immediately add to liveEvents
      addLiveEvent(event);
      if (event.client_id) {
        pushUndoCandidate(event.client_id);
      }

      // Step 9: Send/Queue Logic
      //
      // Gates on wsRef.current.readyState alone, NOT the store's
      // isConnected flag. isConnected is set by setConnected() exclusively
      // inside onopen/onclose/connect-failure handlers (useMatchSocket.ts)
      // — a same-value mirror of the physical socket state that can lag it
      // by a render, the same class of stale-closure gap this codebase has
      // hit before (cockpit handlers reading render-closure props instead
      // of getState()). The backend protocol has no room-join/subscription
      // handshake — manager.connect() (websocket.py) accepts and registers
      // the socket, and the receive loop starts immediately with no ack
      // sent back before the client may send — so isConnected carries no
      // application-level meaning beyond transport open/closed that
      // readyState doesn't already capture, live and without the lag.
      // Reading the ref directly here means an event can never be
      // misrouted to the offline queue while the socket is genuinely open.
      // NOTE: this does not fix the infinite-retry defect (task #37) — an
      // event rejected by server-side validation still retries forever
      // once queued. It only reduces how often an event takes the queue
      // path from a stale-closure misread in the first place.
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        try {
          wsRef.current.send(JSON.stringify(event));
          registerPendingAck(event, "live");
          console.log("✓ Event sent:", event.type);
        } catch (error) {
          // PMA-COCK-205 (EPIC-43, judge round 1, P6): send() threw before
          // registerPendingAck ran, so this event was never actually
          // transmitted and never got a pendingAcks entry -- the same
          // "genuinely never sent" shape as the offline branch just below,
          // just reached via a synchronous send failure (e.g. a TOCTOU race
          // as the socket transitions to CLOSING between the readyState
          // check above and this call) instead of a readyState check.
          // addQueuedEvent here, with no prior removal from `liveEvents`
          // (already populated by Step 8's unconditional addLiveEvent
          // above), is the SAME intended dual-membership as the offline
          // branch -- not a third defect alongside expireAck/
          // flushPendingAcksToQueue. Verified via the real mechanism (a
          // forced synchronous send() throw), not assumed from the offline
          // branch's precedent: useMatchSocket.sendFailureOverlapDedup
          // .test.ts. See useLogIntegrityChecks.ts's dedupeByStableIdentity
          // comment, which already covers both routes generically.
          console.error("✗ Send failed, queuing event:", error);
          addQueuedEvent(event);
        }
      } else {
        // Not connected - immediately queue to IndexedDB. PMA-COCK-205: the
        // documented-intentional overlap route (dedupeByStableIdentity's
        // comment) -- see the catch branch above for its sibling route.
        console.log("⚠ Offline, queuing event:", event.type);
        addQueuedEvent(event);
      }
    },
    [matchId, addLiveEvent, addQueuedEvent, registerPendingAck],
  );

  const undoEvent = useCallback(
    (event: MatchEvent) => {
      if (!event?.client_id) {
        throw new Error("client_id required to undo an event");
      }
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        throw new Error("WebSocket is not connected");
      }

      const alreadyPending = Boolean(pendingAcksRef.current?.[event.client_id]);
      if (!alreadyPending) {
        if (!pendingAckOrderRef.current.includes(event.client_id)) {
          pendingAckOrderRef.current.push(event.client_id);
        }
        upsertPendingAck(event.client_id, { event, source: "live" });
      }

      undoInFlightRef.current.add(event.client_id);
      // PMA-COCK-147: an in-flight undo blocks isSubmitting exactly like any
      // other pending ack (computeIsSubmitting doesn't special-case undo
      // entries), so it needs the same "can never permanently disable the
      // cockpit" guarantee. expireAck checks undoInFlightRef and, on
      // expiry, only clears tracking here -- it deliberately never re-sends
      // the underlying event as a fresh send (that would resurrect a
      // deleted event as a duplicate); see expireAck's own comment.
      startAckTimeout(event.client_id);

      wsRef.current.send(
        JSON.stringify({
          command: "undo",
          match_id: matchId,
          client_id: event.client_id,
          event_id: event._id,
        }),
      );
    },
    [matchId, upsertPendingAck, startAckTimeout],
  );

  /**
   * Initialize WebSocket connection
   */
  const connect = useCallback(async () => {
    if (!enabled || !matchId) return;

    try {
      const token = await getAuthToken();
      if (!token) {
        return;
      }

      // WebSocket URL: ws://localhost:8000/ws/{match_id} — no token here.
      // PMA-EVENT-020 (event-system-contract.md, EPIC-30): the Firebase ID
      // token is sent as the sole `Sec-WebSocket-Protocol` value below, not
      // a URL query parameter, so it never reaches Cloud Run's own request
      // log (`httpRequest.requestUrl`, OQ-SEC-01). Do NOT also append
      // `?token=` to wsUrl — since EPIC-56 the server accepts only the
      // subprotocol transport; a query-string token is ignored and a
      // handshake without the subprotocol is rejected. Sending both here
      // would leak the token into request logs for no benefit.
      const wsBase = resolveWsBaseUrl();
      const wsUrl = `${wsBase}/ws/${matchId}`;

      // Close any existing connection before creating a new one to avoid
      // orphaned WebSockets (e.g. during React StrictMode remounts).
      if (wsRef.current) {
        const prev = wsRef.current;
        wsRef.current = null;
        prev.onclose = null;
        prev.onmessage = null;
        prev.onerror = null;
        prev.close();
      }

      // wsUrl no longer carries the token (see above), but `token` itself
      // is still live and secret, offered here as the WS subprotocol. Per
      // RFC 6455 §4.1 the server must echo one of the offered subprotocols
      // back, so after `onopen`, `ws.protocol` holds this SAME live
      // Firebase ID token from the other side of the handshake — never log
      // wsUrl, `token`, or `ws.protocol`, here or in any E2E harness code.
      console.log("Connecting to WebSocket for match:", matchId);
      const ws = new WebSocket(wsUrl, [token]);
      wsRef.current = ws;
      connectAttemptCountRef.current += 1;
      const isReconnectAttempt = connectAttemptCountRef.current > 1;

      const handleOpen = () => {
        // Guard: only process if this WS is still the current one
        if (wsRef.current !== ws) return;
        console.log("✓ WebSocket connected");
        suppressReconnectRef.current = false;
        reconnectAttemptRef.current = 0;
        setConnected(true);
        syncQueue();
        // #93: broadcast_to_match (backend) has no backlog/replay -- a
        // client that is not in the room at broadcast time never receives
        // that message, and every reconnect below closes and reopens room
        // membership. That makes every reconnect a window where events
        // broadcast by other loggers during the gap are silently and
        // permanently lost, with no client-visible signal anything was
        // missed. Rehydrating from the server (requestTimelineRefresh,
        // which the cockpit's hydrateEvents effect turns into a GET
        // /matches/:id/events fetch) closes that window by pulling the
        // authoritative event list right after (re)joining.
        //
        // Only when isReconnectAttempt (this hook instance's Nth
        // WebSocket, N > 1), never unconditionally on every open:
        // useCockpitLifecycleEffects.ts already runs its own hydrateEvents
        // on mount as soon as matchId is set, independent of socket state,
        // so a rehydrate on the very first open would just be a redundant
        // fetch racing that one. connectAttemptCountRef increments in
        // connect() itself, not in an onopen/onclose handler, so
        // isReconnectAttempt is true for every path that reaches a second
        // (or later) connect() call: the auto-reconnect backoff below
        // (including when the very first connect attempt failed to open
        // and only a retry succeeded), an explicit E2E-harness
        // reconnect(), and a React StrictMode dev-mode remount (whose
        // disconnect() teardown nulls ws.onclose before closing, so it
        // never reaches onclose's own bookkeeping) alike.
        if (isReconnectAttempt) {
          requestTimelineRefresh();
        }
      };

      // Step 6: On-Connect Handler (Section 4.3)
      ws.onopen = handleOpen;
      if (IS_E2E_TEST_MODE) {
        handleOpen();
      }

      // Step 10: On-Disconnect Handler (Section 4.3)
      ws.onclose = (event) => {
        // Guard: only process if this WS is still the current one.
        // Prevents stale WS onclose from tearing down a newer connection.
        if (wsRef.current !== null && wsRef.current !== ws) return;
        console.log("✗ WebSocket disconnected:", event.code, event.reason);
        flushPendingAcksToQueue();
        pendingAckOrderRef.current = [];
        setConnected(false); // Update store

        // If the disconnect was forced (E2E harness) keep the socket offline
        // until an explicit reconnect() call is issued via the harness.
        if (suppressReconnectRef.current) {
          return;
        }

        // Attempt reconnection after 3 seconds
        const backoffMs = 3000;
        reconnectAttemptRef.current += 1;
        const attemptNumber = reconnectAttemptRef.current;
        trackTelemetry("ws_reconnect_attempt", {
          matchId,
          attempt_number: attemptNumber,
          backoff_ms: backoffMs,
        });
        reconnectTimeoutRef.current = setTimeout(() => {
          console.log("Attempting to reconnect...");
          connect();
        }, backoffMs);
      };

      // Handle incoming messages (confirmations, broadcasts)
      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);

          // PMA-COCK-218/PMA-EVENT-020: this used to reject `message`/
          // `message.result` here when its `match_id`/`matchId` field
          // disagreed with this hook's OWN `matchId` param -- comparing
          // the server's always-CANONICAL business key against whatever
          // id shape the calling page's route uses (the Mongo `_id` for
          // `LiveMatch.tsx`'s `/matches/:matchId/live`), which is a
          // different domain and could never agree there. No re-check is
          // needed: `broadcast_to_match` (app/websocket.py) only ever
          // fans a message out to sockets already registered in that
          // exact canonical match's room (`active_connections[match_id]`,
          // keyed off the SAME canonical id `_ensure_match_access`
          // resolves once at connect and threads through the rest of the
          // connection's lifetime, EPIC-82) -- this socket only ever
          // joined ONE room for its whole life (opened with a fixed
          // `matchId`, torn down and reopened on change), so every
          // message it receives already belongs to that room by
          // construction. A client-side re-check using a DIFFERENT id
          // domain could only ever produce a false negative here, never a
          // true positive, which is exactly what made LiveMatch.tsx's
          // timeline silently drop every WS-delivered event and
          // `match_state_changed` refresh.

          // Handle acknowledgment
          if (message.type === "ack") {
            handleAckResult(message.result ?? {});
            return;
          }

          // Handle broadcast events from any logger
          if (message._confirmed) {
            upsertLiveEvent(message as MatchEvent);
            return;
          }

          if (message.type === "event_undone") {
            if (message.event_id) {
              removeLiveEventById(message.event_id);
            }
            if (message.client_id) {
              removeLiveEventByClientId(message.client_id);
              removeQueuedEventByClientId(message.client_id);
              removeUndoCandidate(message.client_id);
            }
            return;
          }

          // Handle match state changes (clock mode, status, period) from other tabs
          if (message.type === "match_state_changed") {
            requestTimelineRefresh();
            requestMatchRefresh();
            return;
          }
        } catch (error) {
          console.error("Error parsing WebSocket message:", error);
        }
      };

      // Handle errors
      ws.onerror = (error) => {
        console.error("WebSocket error:", error);
      };
    } catch (error) {
      console.error("Failed to connect WebSocket:", error);
      setConnected(false);
    }
  }, [
    enabled,
    matchId,
    setConnected,
    syncQueue,
    flushPendingAcksToQueue,
    handleAckResult,
    upsertLiveEvent,
    getAuthToken,
    requestTimelineRefresh,
  ]);

  /**
   * Disconnect WebSocket
   */
  const disconnect = useCallback(() => {
    flushPendingAcksToQueue();
    pendingAckOrderRef.current = [];
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    if (wsRef.current) {
      const ws = wsRef.current;
      wsRef.current = null;
      // Nullify handlers BEFORE closing to prevent stale onclose/onopen
      // from interfering with future connections (React StrictMode safety).
      ws.onclose = null;
      ws.onmessage = null;
      ws.onerror = null;
      ws.close();
    }

    setConnected(false);
  }, [flushPendingAcksToQueue, setConnected]);

  /**
   * Effect: Connect on mount, disconnect on unmount
   */
  useEffect(() => {
    connect();

    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  useEffect(() => {
    if (isConnected) {
      syncQueue();
    }
  }, [isConnected, matchId, syncQueue]);

  // Periodic retry — re-send queued events every 10 s while connected so
  // that a transient backend error does not permanently strand events.
  useEffect(() => {
    if (!isConnected) return;
    const id = setInterval(syncQueue, 10_000);
    return () => clearInterval(id);
  }, [isConnected, syncQueue]);

  useEffect(() => {
    if (!IS_E2E_TEST_MODE) {
      return;
    }

    const harness: SocketTestHarness = {
      forceDisconnect: () => {
        suppressReconnectRef.current = true;
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
        if (wsRef.current) {
          wsRef.current.close(4001, "e2e-force-disconnect");
        }
      },
      reconnect: () => {
        suppressReconnectRef.current = false;
        connect();
      },
    };

    window.__PROMATCH_SOCKET_TEST__ = harness;

    return () => {
      if (window.__PROMATCH_SOCKET_TEST__ === harness) {
        delete window.__PROMATCH_SOCKET_TEST__;
      }
    };
  }, [connect]);

  return {
    sendEvent,
    undoEvent,
    isConnected,
  };
};
